import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, AlertTriangle,
  User, Stethoscope, MapPin, Clock, DollarSign, ExternalLink, CalendarClock
} from "lucide-react";
import { format, startOfWeek, endOfWeek, addDays, isSameDay, isToday, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

import { API } from "@/config/api";

const REPS = ["A", "B", "C", "D"];
const AREAS = ["PTA", "JHB", "CPT", "PE", "Other"];

// 10 Pipeline Stages with colors per user requirements
const STAGE_CONFIG = {
  "Referrals": { color: "bg-blue-100 text-blue-800 border-blue-300", bgLight: "bg-blue-50", dotColor: "bg-blue-500" },
  "Pre SS": { color: "bg-orange-100 text-orange-800 border-orange-300", bgLight: "bg-orange-50", dotColor: "bg-orange-500" },
  "SS Authorization": { color: "bg-teal-100 text-teal-800 border-teal-300", bgLight: "bg-teal-50", dotColor: "bg-teal-500" },
  "SS Facilitation": { color: "bg-purple-100 text-purple-800 border-purple-300", bgLight: "bg-purple-50", dotColor: "bg-purple-500" },
  "Post SS": { color: "bg-indigo-100 text-indigo-800 border-indigo-300", bgLight: "bg-indigo-50", dotColor: "bg-indigo-500" },
  "SS Reports": { color: "bg-amber-100 text-amber-800 border-amber-300", bgLight: "bg-amber-50", dotColor: "bg-amber-700" },
  "TT Facilitation": { color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", bgLight: "bg-fuchsia-50", dotColor: "bg-fuchsia-500" },
  "Post TT": { color: "bg-yellow-100 text-yellow-800 border-yellow-300", bgLight: "bg-yellow-50", dotColor: "bg-yellow-500" },
  "TT Sum": { color: "bg-gray-100 text-gray-800 border-gray-300", bgLight: "bg-gray-50", dotColor: "bg-gray-500" },
  "TT Report": { color: "bg-emerald-100 text-emerald-800 border-emerald-300", bgLight: "bg-emerald-50", dotColor: "bg-emerald-500" }
};

const CalendarPage = () => {
  const navigate = useNavigate();
  const [calendarData, setCalendarData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week"); // day or week
  
  // Filters
  const [filterRep, setFilterRep] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterArea, setFilterArea] = useState("");
  
  // Case detail dialog
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetailOpen, setCaseDetailOpen] = useState(false);

  useEffect(() => {
    fetchCalendarData();
  }, [filterRep, filterStage, filterArea]);

  const fetchCalendarData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterRep) params.append("rep", filterRep);
      if (filterStage) params.append("stage", filterStage);
      if (filterArea) params.append("area", filterArea);
      
      const response = await axios.get(`${API}/pipeline/calendar-data?${params.toString()}`);
      setCalendarData(response.data);
    } catch (error) {
      toast.error("Failed to fetch calendar data");
    } finally {
      setLoading(false);
    }
  };

  const { items, summary } = calendarData;

  // Get cases for a specific date
  const getCasesForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return items.filter(item => item.next_action_date === dateStr);
  };

  // Get overdue cases
  const overdueCases = useMemo(() => {
    return items.filter(item => item.is_overdue);
  }, [items]);

  // Get needs scheduling cases
  const needsSchedulingCases = useMemo(() => {
    return items.filter(item => item.needs_scheduling);
  }, [items]);

  // Week dates
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Clear all filters
  const clearFilters = () => {
    setFilterRep("");
    setFilterStage("");
    setFilterArea("");
  };

  const hasFilters = filterRep || filterStage || filterArea;

  // Navigation
  const goToToday = () => setCurrentDate(new Date());
  const goPrev = () => setCurrentDate(addDays(currentDate, viewMode === "day" ? -1 : -7));
  const goNext = () => setCurrentDate(addDays(currentDate, viewMode === "day" ? 1 : 7));

  // Open case detail
  const handleCaseClick = (caseItem) => {
    setSelectedCase(caseItem);
    setCaseDetailOpen(true);
  };

  // Navigate to Pipeline page
  const handleOpenFullCase = () => {
    setCaseDetailOpen(false);
    if (selectedCase?.tin_number) {
      navigate(`/cases/${selectedCase.tin_number}`);
    } else {
      navigate("/referrals");
    }
  };

  // Auto-filter by rep/area when clicked in modal
  const handleFilterByRep = (rep) => {
    setFilterRep(rep);
    setCaseDetailOpen(false);
  };

  const handleFilterByArea = (area) => {
    setFilterArea(area);
    setCaseDetailOpen(false);
  };

  // Calendar item component
  const CalendarItem = ({ caseItem, compact = false }) => {
    const stageConfig = STAGE_CONFIG[caseItem.current_stage] || STAGE_CONFIG["Referrals"];
    
    return (
      <div
        onClick={() => handleCaseClick(caseItem)}
        data-testid={`calendar-item-${caseItem.tin_number}`}
        className={cn(
          "p-2 rounded-lg border cursor-pointer transition-all hover:shadow-md",
          stageConfig.color,
          caseItem.is_overdue && "ring-2 ring-red-500 ring-offset-1"
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className={cn("font-medium truncate", compact ? "text-xs" : "text-sm")}>
              {caseItem.name} {caseItem.surname}
            </p>
            {!compact && (
              <>
                <p className="text-xs opacity-75 truncate">Dr. {caseItem.doctor}</p>
                <div className="flex items-center gap-2 mt-1">
                  {caseItem.assigned_rep && (
                    <span className="text-xs flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Rep {caseItem.assigned_rep}
                    </span>
                  )}
                  <span className="text-xs">{caseItem.area}</span>
                </div>
              </>
            )}
          </div>
          {caseItem.is_overdue && (
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
          )}
          {caseItem.needs_scheduling && !caseItem.is_overdue && (
            <CalendarClock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          )}
          {caseItem.commission_eligible && !caseItem.is_overdue && !caseItem.needs_scheduling && (
            <DollarSign className="w-3 h-3 text-emerald-600 flex-shrink-0" />
          )}
        </div>
      </div>
    );
  };

  // Day View
  const DayView = () => {
    const todayCases = getCasesForDate(currentDate);
    
    // Group by stage
    const stageGroups = Object.keys(STAGE_CONFIG).reduce((acc, stage) => {
      acc[stage] = todayCases.filter(c => c.current_stage === stage);
      return acc;
    }, {});

    // Overdue cases for this day (including past overdue)
    const overdueForDay = overdueCases.filter(c => {
      try {
        const dueDate = parseISO(c.next_action_date);
        return dueDate <= currentDate;
      } catch {
        return false;
      }
    });

    return (
      <div className="space-y-4">
        {/* Date Header */}
        <div className="text-center">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {format(currentDate, "EEEE, MMMM d, yyyy")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {todayCases.length} cases due {isToday(currentDate) && `• ${overdueForDay.length} overdue`}
          </p>
        </div>

        {/* Overdue Section (Pinned to Top) */}
        {isToday(currentDate) && overdueForDay.length > 0 && (
          <Card className="border-red-300 bg-red-50">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                Overdue ({overdueForDay.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {overdueForDay.map(c => (
                  <CalendarItem key={c.case_id} caseItem={c} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stage Groups */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(stageGroups).map(([stage, stageCases]) => {
            if (stageCases.length === 0) return null;
            const config = STAGE_CONFIG[stage];
            return (
              <Card key={stage} className={cn("border", config.bgLight)}>
                <CardHeader className="py-3">
                  <CardTitle className={cn("text-sm flex items-center justify-between", config.color.split(' ')[1])}>
                    <span>{stage}</span>
                    <Badge variant="secondary">{stageCases.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {stageCases.map(c => (
                    <CalendarItem key={c.case_id} caseItem={c} />
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {todayCases.length === 0 && (isToday(currentDate) ? overdueForDay.length === 0 : true) && (
          <div className="text-center py-12 text-muted-foreground">
            <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No cases due on this date</p>
          </div>
        )}
      </div>
    );
  };

  // Week View - Mobile optimized with horizontal scroll
  const WeekView = () => {
    return (
      <div className="space-y-3 sm:space-y-4">
        {/* Week Header */}
        <div className="text-center">
          <h2 className="text-base sm:text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Week of {format(weekStart, "MMM d")} - {format(endOfWeek(weekStart, { weekStartsOn: 1 }), "MMM d, yyyy")}
          </h2>
        </div>

        {/* Week Grid - Horizontal scroll on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-[600px] sm:min-w-0">
            {weekDates.map((date, idx) => {
              const dayCases = getCasesForDate(date);
              const dayOverdue = dayCases.filter(c => c.is_overdue);
              const isCurrentDay = isToday(date);
              
              return (
                <div
                  key={idx}
                  onClick={() => {
                    // On mobile, clicking a day opens it in Day view
                    if (window.innerWidth < 640) {
                      setCurrentDate(date);
                      setViewMode("day");
                    }
                  }}
                  className={cn(
                    "border rounded-lg min-h-[140px] sm:min-h-[200px] flex flex-col cursor-pointer sm:cursor-default",
                    isCurrentDay && "border-primary border-2"
                  )}
                >
                  {/* Day Header */}
                  <div className={cn(
                    "p-1.5 sm:p-2 border-b text-center",
                    isCurrentDay ? "bg-primary/10" : "bg-muted/30"
                  )}>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      {format(date, "EEE")}
                    </p>
                    <p className={cn(
                      "text-sm sm:text-lg font-semibold",
                      isCurrentDay && "text-primary"
                    )}>
                      {format(date, "d")}
                    </p>
                    {dayCases.length > 0 && (
                      <Badge variant="secondary" className="text-[9px] sm:text-xs mt-0.5 sm:mt-1 h-4 sm:h-5 px-1">
                        {dayCases.length}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Day Cases - Limited on mobile */}
                  <div className="flex-1 p-1 space-y-0.5 sm:space-y-1 overflow-y-auto max-h-[100px] sm:max-h-[300px]">
                    {/* Overdue first */}
                    {dayOverdue.slice(0, 3).map(c => (
                      <div
                        key={c.case_id}
                        onClick={(e) => { e.stopPropagation(); handleCaseClick(c); }}
                        className="p-1 sm:p-1.5 rounded text-[10px] sm:text-xs bg-red-100 border border-red-300 cursor-pointer hover:bg-red-200 truncate"
                      >
                        <div className="flex items-center gap-0.5 sm:gap-1">
                          <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-600 flex-shrink-0" />
                          <span className="truncate font-medium">{c.name}</span>
                        </div>
                      </div>
                    ))}
                    {/* Regular cases */}
                    {dayCases.filter(c => !c.is_overdue).slice(0, window.innerWidth < 640 ? 2 : 10).map(c => {
                      const config = STAGE_CONFIG[c.current_stage];
                      return (
                        <div
                          key={c.case_id}
                          onClick={(e) => { e.stopPropagation(); handleCaseClick(c); }}
                          className={cn(
                            "p-1 sm:p-1.5 rounded text-[10px] sm:text-xs cursor-pointer truncate border",
                            config?.color || "bg-gray-100"
                          )}
                        >
                          <span className="truncate font-medium">{c.name}</span>
                          <span className="text-[8px] sm:text-[10px] block opacity-75 hidden sm:block">
                            {c.assigned_rep && `Rep ${c.assigned_rep}`}
                          </span>
                        </div>
                      );
                    })}
                    {/* Show more indicator on mobile */}
                    {dayCases.length > 3 && (
                      <div className="text-center text-[9px] text-muted-foreground sm:hidden">
                        +{dayCases.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stage Legend - Compact on mobile */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center">
          {Object.entries(STAGE_CONFIG).map(([stage, config]) => (
            <div key={stage} className="flex items-center gap-0.5 sm:gap-1">
              <div className={cn("w-2 h-2 sm:w-3 sm:h-3 rounded", config.dotColor)} />
              <span className="text-[9px] sm:text-xs text-muted-foreground">{stage}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 lg:p-6 space-y-3 sm:space-y-4 pb-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Referral Calendar
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Management view of due dates and pipeline stages</p>
        </div>
        
        {/* View Toggle - Prominent on mobile */}
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "day" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("day")}
            className="flex-1 sm:flex-initial h-10 sm:h-9"
            data-testid="view-day-button"
          >
            Day
          </Button>
          <Button
            variant={viewMode === "week" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("week")}
            className="flex-1 sm:flex-initial h-10 sm:h-9"
            data-testid="view-week-button"
          >
            Week
          </Button>
        </div>
      </div>

      {/* Overdue Summary */}
      {summary.overdue > 0 && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-2.5 sm:p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
              <span className="font-medium text-red-800 text-xs sm:text-sm">
                {summary.overdue} overdue case{summary.overdue > 1 ? 's' : ''} requiring attention
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-300 w-full sm:w-auto h-9"
              onClick={() => {
                setViewMode("day");
                setCurrentDate(new Date());
              }}
              data-testid="view-overdue-button"
            >
              View All
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Needs Scheduling Banner */}
      {summary.needs_scheduling > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-2.5 sm:p-3 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
            <span className="font-medium text-amber-800 text-xs sm:text-sm">
              {summary.needs_scheduling} case{summary.needs_scheduling > 1 ? 's' : ''} need scheduling
            </span>
          </CardContent>
        </Card>
      )}

      {/* Filters & Navigation */}
      <Card>
        <CardContent className="p-2.5 sm:p-3">
          <div className="flex flex-col gap-3">
            {/* Navigation - Top row on mobile */}
            <div className="flex items-center justify-between sm:justify-start gap-2">
              <Button variant="outline" size="icon" onClick={goPrev} className="h-10 w-10 sm:h-9 sm:w-9" data-testid="nav-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday} className="flex-1 sm:flex-initial h-10 sm:h-9" data-testid="nav-today">
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={goNext} className="h-10 w-10 sm:h-9 sm:w-9" data-testid="nav-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Filters - Horizontal scroll on mobile */}
            <div className="flex items-center gap-2 overflow-x-auto -mx-2.5 px-2.5 sm:mx-0 sm:px-0 sm:flex-wrap pb-1 sm:pb-0">
              <Select value={filterRep} onValueChange={setFilterRep}>
                <SelectTrigger className="w-24 sm:w-28 h-10 sm:h-8 text-xs flex-shrink-0" data-testid="filter-rep">
                  <SelectValue placeholder="All Reps" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {REPS.map(rep => (
                    <SelectItem key={rep} value={rep}>Rep {rep}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStage} onValueChange={setFilterStage}>
                <SelectTrigger className="w-28 sm:w-32 h-10 sm:h-8 text-xs flex-shrink-0" data-testid="filter-stage">
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {Object.keys(STAGE_CONFIG).map(stage => (
                    <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterArea} onValueChange={setFilterArea}>
                <SelectTrigger className="w-24 sm:w-28 h-10 sm:h-8 text-xs flex-shrink-0" data-testid="filter-area">
                  <SelectValue placeholder="All Areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Areas</SelectItem>
                  {AREAS.map(area => (
                    <SelectItem key={area} value={area}>{area}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-10 sm:h-8 text-xs gap-1 flex-shrink-0"
                  data-testid="clear-filters-button"
                >
                  <X className="w-3 h-3" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <motion.div
          key={`${viewMode}-${currentDate.toISOString()}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {viewMode === "day" ? <DayView /> : <WeekView />}
        </motion.div>
      )}

      {/* Case Detail Dialog */}
      <Dialog open={caseDetailOpen} onOpenChange={setCaseDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="case-detail-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Case Details
            </DialogTitle>
          </DialogHeader>
          {selectedCase && (
            <div className="space-y-4">
              {/* Case Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold">
                    {selectedCase.name} {selectedCase.surname}
                  </h3>
                  <p className="text-sm text-muted-foreground font-mono">
                    TIN: {selectedCase.tin_number}
                  </p>
                </div>
                <div className="text-right space-y-1">
                  <Badge className={cn(STAGE_CONFIG[selectedCase.current_stage]?.color)}>
                    {selectedCase.current_stage}
                  </Badge>
                  {selectedCase.is_overdue && (
                    <Badge variant="destructive" className="ml-2">
                      Overdue
                    </Badge>
                  )}
                  {selectedCase.needs_scheduling && (
                    <Badge variant="outline" className="ml-2 border-amber-400 text-amber-600">
                      Needs Scheduling
                    </Badge>
                  )}
                </div>
              </div>

              {/* Case Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Doctor</p>
                    <p className="text-sm font-medium">{selectedCase.doctor}</p>
                  </div>
                </div>
                <div 
                  className="flex items-center gap-2 cursor-pointer hover:bg-muted/80 rounded p-1 -m-1"
                  onClick={() => selectedCase.assigned_rep && handleFilterByRep(selectedCase.assigned_rep)}
                >
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Assigned Rep</p>
                    <p className="text-sm font-medium text-primary underline-offset-2 hover:underline">
                      {selectedCase.assigned_rep || "-"}
                    </p>
                  </div>
                </div>
                <div 
                  className="flex items-center gap-2 cursor-pointer hover:bg-muted/80 rounded p-1 -m-1"
                  onClick={() => handleFilterByArea(selectedCase.area)}
                >
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Area</p>
                    <p className="text-sm font-medium text-primary underline-offset-2 hover:underline">
                      {selectedCase.area}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Due Date</p>
                    <p className={cn("text-sm font-medium", selectedCase.is_overdue && "text-red-600")}>
                      {selectedCase.next_action_date || "Not set"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Medical Aid */}
              {selectedCase.medical_aid && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600">Medical Aid</p>
                  <p className="text-sm font-medium text-blue-800">{selectedCase.medical_aid}</p>
                </div>
              )}

              {/* Commission Status */}
              {selectedCase.commission_eligible && (
                <div className="p-3 bg-emerald-50 rounded-lg flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Commission Eligible</p>
                    <p className="text-xs text-emerald-600">Triggered at: {selectedCase.commission_stage}</p>
                  </div>
                </div>
              )}

              {/* Stage Timeline */}
              <div>
                <p className="text-sm font-medium mb-2">Stage Timeline</p>
                <div className="space-y-1 text-xs">
                  {selectedCase.referral_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Referral</span>
                      <span>{selectedCase.referral_date}</span>
                    </div>
                  )}
                  {selectedCase.pre_ss_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pre SS</span>
                      <span>{selectedCase.pre_ss_date}</span>
                    </div>
                  )}
                  {selectedCase.ss_auth_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">SS Authorization</span>
                      <span>{selectedCase.ss_auth_date}</span>
                    </div>
                  )}
                  {selectedCase.ss_facilitation_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">SS Facilitation</span>
                      <span>{selectedCase.ss_facilitation_date}</span>
                    </div>
                  )}
                  {selectedCase.post_ss_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Post SS</span>
                      <span>{selectedCase.post_ss_date}</span>
                    </div>
                  )}
                  {selectedCase.ss_reports_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">SS Reports</span>
                      <span>{selectedCase.ss_reports_date}</span>
                    </div>
                  )}
                  {selectedCase.tt_facilitation_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TT Facilitation</span>
                      <span>{selectedCase.tt_facilitation_date}</span>
                    </div>
                  )}
                  {selectedCase.post_tt_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Post TT</span>
                      <span>{selectedCase.post_tt_date}</span>
                    </div>
                  )}
                  {selectedCase.tt_sum_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TT Sum</span>
                      <span>{selectedCase.tt_sum_date}</span>
                    </div>
                  )}
                  {selectedCase.tt_report_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">TT Report</span>
                      <span>{selectedCase.tt_report_date}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Open Full Case Button */}
              <div className="pt-2 border-t">
                <Button 
                  className="w-full gap-2" 
                  onClick={handleOpenFullCase}
                  data-testid="open-full-case-button"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open Full Case in Pipeline
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
